import React, { useState, useCallback, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useForm, Controller } from 'react-hook-form';
import { CustomInput } from '../components/CustomInput';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import ApiService from '../services/ApiService';
import { DairyPurchase } from '../models/types';
import { generateId, numericValidation, alphabetOnlyWithSpace } from '../utils/helpers';

interface DairyPurchaseFormData {
    date: string;
    itemName: string;
    quantity: string;
    pricePerUnit: string;
    source: string;
    notes: string;
}

const AddEditDairyPurchaseScreen = ({ navigation, route }: any) => {
    const existingPurchase = route.params?.purchase as DairyPurchase | undefined;
    const isEditMode = !!existingPurchase;

    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        isSuccess?: boolean;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<DairyPurchaseFormData>({
        defaultValues: {
            date: existingPurchase?.date || new Date().toISOString(),
            itemName: existingPurchase?.itemName || '',
            quantity: existingPurchase?.quantity?.toString() || '1',
            pricePerUnit: existingPurchase?.pricePerUnit?.toString() || '',
            source: existingPurchase?.source || '',
            notes: existingPurchase?.notes || '',
        },
        mode: 'onChange',
    });

    const quantity = watch('quantity');
    const pricePerUnit = watch('pricePerUnit');

    // Calculate total amount dynamically
    const totalAmount = (Number(quantity) || 0) * (Number(pricePerUnit) || 0);

    const handleModalClose = () => {
        const isSuccess = modal.isSuccess;
        setModal(prev => ({ ...prev, visible: false }));
        if (isSuccess) {
            navigation.goBack();
        }
    };

    const onSubmit = useCallback(async (data: DairyPurchaseFormData) => {
        setLoading(true);
        try {
            const now = new Date().toISOString();
            const calculatedTotal = (Number(data.quantity) || 0) * (Number(data.pricePerUnit) || 0);
            
            const purchase: DairyPurchase = {
                id: existingPurchase?.id || generateId('DPUR'),
                itemName: data.itemName.trim(),
                date: data.date,
                quantity: Number(data.quantity),
                pricePerUnit: Number(data.pricePerUnit),
                totalAmount: calculatedTotal,
                source: data.source.trim() || undefined,
                notes: data.notes.trim() || undefined,
                createdAt: existingPurchase?.createdAt || now,
                updatedAt: now,
            };

            if (isEditMode) {
                await ApiService.updateDairyPurchase(purchase.id, purchase);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Purchase updated successfully',
                    isSuccess: true,
                });
            } else {
                await ApiService.addDairyPurchase(purchase);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Purchase added successfully',
                    isSuccess: true,
                });
            }
        } catch (error) {
            console.error('Error saving purchase:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to save purchase. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, [existingPurchase, isEditMode, navigation]);

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title={isEditMode ? 'Edit Purchase' : 'Add Purchase'}
                showBackButton={true}
                navigation={navigation}
            />

            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    <Controller
                        control={control}
                        name="date"
                        rules={{ required: 'Purchase date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Purchase Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.date?.message}
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="itemName"
                        rules={{ 
                            required: 'Item name is required',
                            validate: (value) => value.trim().length > 0 || 'Item name cannot be only spaces'
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Item Name *"
                                value={value}
                                onChangeText={(text) => onChange(alphabetOnlyWithSpace(text))}
                                onBlur={onBlur}
                                icon="package-variant"
                                error={errors.itemName?.message}
                                placeholder="e.g., Cattle Fodder, Hay, etc."
                            />
                        )}
                    />

                    <View style={styles.row}>
                        <View style={styles.halfInput}>
                            <Controller
                                control={control}
                                name="quantity"
                                rules={{ required: 'Quantity is required' }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <CustomInput
                                        label="Quantity *"
                                        value={value}
                                        onChangeText={(text) => onChange(numericValidation(text))}
                                        onBlur={onBlur}
                                        icon="scale"
                                        keyboardType="number-pad"
                                        error={errors.quantity?.message}
                                        placeholder="e.g., 2"
                                    />
                                )}
                            />
                        </View>
                        <View style={styles.halfInput}>
                            <Controller
                                control={control}
                                name="pricePerUnit"
                                rules={{ required: 'Price is required' }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <CustomInput
                                        label="Price/Item (₹) *"
                                        value={value}
                                        onChangeText={(text) => onChange(numericValidation(text))}
                                        onBlur={onBlur}
                                        icon="currency-inr"
                                        keyboardType="number-pad"
                                        error={errors.pricePerUnit?.message}
                                        placeholder="e.g., 500"
                                    />
                                )}
                            />
                        </View>
                    </View>

                    {/* Total Amount Display */}
                    <View style={styles.totalContainer}>
                        <Text style={styles.totalLabel}>Total Amount:</Text>
                        <Text style={styles.totalValue}>₹ {totalAmount.toFixed(2)}</Text>
                    </View>

                    <Controller
                        control={control}
                        name="source"
                        rules={{ 
                            required: 'Source/Supplier is required',
                            validate: (value) => value.trim().length > 0 || 'Source/Supplier cannot be only spaces'
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Source/Supplier *"
                                value={value}
                                onChangeText={(text) => onChange(alphabetOnlyWithSpace(text))}
                                onBlur={onBlur}
                                icon="store"
                                error={errors.source?.message}
                                placeholder="e.g., Local Farm Supply"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Notes (Optional)"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={3}
                            />
                        )}
                    />

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                            disabled={loading}
                        >
                            <Text style={styles.saveBtnText}>
                                {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={handleModalClose}
            />

            <LoadingOverlay visible={loading} message="Saving..." />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.lg,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
    row: {
        flexDirection: 'row',
        gap: theme.spacing.md,
    },
    halfInput: {
        flex: 1,
    },
    totalContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing.md,
        backgroundColor: theme.colors.primary + '10',
        borderRadius: theme.borderRadius.md,
        marginBottom: theme.spacing.md,
        borderWidth: 1,
        borderColor: theme.colors.primary + '30',
    },
    totalLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    totalValue: {
        ...theme.typography.h5,
        color: theme.colors.primary,
        fontWeight: '700',
    },
});

export default AddEditDairyPurchaseScreen;
